/**
 * The shell's notification bell (OneFleet WP-A9 / WP-U15 remainder, D-80): for anyone holding a fleet role, the
 * unread count of their OneFleet in-app notifications (mine and my roles') and a tray with the latest ones —
 * open the link, mark read, go to the alerts centre. Polled every minute everywhere; live on the socket's
 * `notification.created` while a `/fleet` page is open (the socket never connects outside the module — D-16).
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router";
import { useAuth } from "../../auth/AuthProvider";
import { useMyNotifications, usePlatformActions, type Notification } from "../../lib/onefleet/platform";
import { FLEET_CONSOLE_ROLES } from "../../lib/onefleet/routes";
import { useFleetEvents } from "../../lib/onefleet/socket";
import { fmtWhen, useFleetCompany } from "./dispatch";

const SEVERITY_DOT: Record<string, string> = { high: "bg-status-red", medium: "bg-amber-500", low: "bg-brand-primary" };
const TRAY = 8;

/** Renders nothing for people without a console role (drivers and customers included), so the other modules'
 * shells stay as they were. */
export function NotificationsBell() {
  const { identity } = useAuth();
  const eligible = !!identity && identity.roles.some((r) => FLEET_CONSOLE_ROLES.includes(r));
  if (!eligible) return null;
  return <Bell />;
}

function Bell() {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { company } = useFleetCompany();
  const inFleet = pathname.startsWith("/fleet");
  const inbox = useMyNotifications(company, !!company, 60_000);
  const act = usePlatformActions(company);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useFleetEvents(["notification.created"], () => act.invalidate(), inFleet);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const unread = inbox.data?.meta.unread ?? 0;
  const rows = (inbox.data?.data ?? []).slice(0, TRAY);
  const openOne = (n: Notification) => {
    if (!n.read_at) act.markRead.mutate(n.id);
    setOpen(false);
    if (n.link) void navigate(n.link);
  };
  const label = unread > 0 ? t("fleet.bell.unread", { count: unread }) : t("fleet.bell.title");
  return (
    <div className="relative" ref={ref} data-fleet-bell={inbox.isPending ? "…" : String(unread)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={label}
        title={label}
        aria-expanded={open}
        aria-haspopup="dialog"
        className={`relative flex h-8 w-8 items-center justify-center rounded-md border text-brand-charcoal transition ${open ? "border-brand-primary bg-brand-primary/10 text-brand-primary" : "border-brand-grey-light hover:border-brand-primary hover:text-brand-primary"}`}
      >
        <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 9a6 6 0 1 1 12 0c0 5 2 6 2 6H4s2-1 2-6" />
          <path d="M10 19a2 2 0 0 0 4 0" />
        </svg>
        {unread > 0 && (
          <span className="absolute -right-1.5 -top-1.5 min-w-[18px] rounded-full bg-status-red px-1 text-center text-[10px] font-bold leading-[18px] text-white" data-fleet-bell-count>
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div role="dialog" aria-label={t("fleet.bell.title")} className="absolute right-0 z-20 mt-2 w-[22rem] max-w-[90vw] rounded-lg border border-brand-grey-light bg-white shadow-lg" data-fleet-bell-tray>
          <div className="flex items-center justify-between border-b border-brand-grey-light px-3 py-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-brand-grey">{t("fleet.bell.title")}</span>
            <span className="text-[11px] text-brand-grey">{unread > 0 ? t("fleet.bell.unread", { count: unread }) : t("fleet.bell.allRead")}</span>
          </div>
          {inbox.isError ? (
            <div className="px-3 py-3 text-xs text-brand-grey">{t("fleet.bell.unavailable")}</div>
          ) : rows.length === 0 ? (
            <div className="px-3 py-3 text-xs text-brand-grey">{t("fleet.bell.empty")}</div>
          ) : (
            <ul className="max-h-[22rem] divide-y divide-brand-grey-light overflow-y-auto text-xs">
              {rows.map((n) => (
                <li key={n.id} data-fleet-bell-item={n.read_at ? "read" : "unread"}>
                  <button type="button" onClick={() => openOne(n)} className={`flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-brand-grey-light/40 ${n.read_at ? "opacity-60" : ""}`}>
                    <span aria-hidden="true" className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${SEVERITY_DOT[n.severity] ?? "bg-brand-grey"}`} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-semibold text-brand-charcoal">{n.title}</span>
                      <span className="block truncate text-brand-grey">{n.body}</span>
                      <span className="block text-[10px] text-brand-grey">{n.created_at ? fmtWhen(n.created_at, i18n.language) : ""}{n.role ? ` · ${n.role}` : ""}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="border-t border-brand-grey-light px-3 py-2 text-right">
            <Link to="/fleet/copilot/alerts" onClick={() => setOpen(false)} className="text-[11px] font-semibold text-brand-primary hover:underline" data-fleet-bell-all>{t("fleet.bell.all")}</Link>
          </div>
        </div>
      )}
    </div>
  );
}
